/**
 * Transport ? help popover — tools, spawn modes, disturb, scripts.
 */

import { BUILTIN_SCRIPTS, DISTURB_HELP } from "../../app/choreography";
import { POINTER_TOOLS } from "../../app/pointerTool";
import { SPAWN_MODES } from "../../app/spawnModes";

/** Build the floating ? popover body. */
export function fillTransportTip(host: HTMLElement): void {
  host.replaceChildren();

  const hTool = document.createElement("h3");
  hTool.textContent = "Tool";
  const pTool = document.createElement("p");
  pTool.textContent =
    "Inspect / Rewire auto-pause on pick so you can aim at 120fps (Space resumes). Spawn stays live while playing — click the stage to add particles. Mode chooses friend/enemy coupling (not particle “types”). Drag still orbits in 3D; wheel dollies. Esc clears selection. With Spawn: m / [ ] cycle modes.";
  const toolUl = document.createElement("ul");
  for (const t of POINTER_TOOLS) {
    const li = document.createElement("li");
    const name = document.createElement("strong");
    name.textContent = t.label;
    li.append(name, document.createTextNode(` — ${t.summary}`));
    li.title = t.detail;
    toolUl.append(li);
  }

  const hMode = document.createElement("h3");
  hMode.textContent = "Spawn mode";
  const modeUl = document.createElement("ul");
  for (const m of SPAWN_MODES) {
    const li = document.createElement("li");
    const name = document.createElement("strong");
    name.textContent = m.label;
    li.append(name, document.createTextNode(` — ${m.summary}`));
    li.title = m.detail;
    modeUl.append(li);
  }

  const pColors = document.createElement("p");
  pColors.className = "tip-muted";
  pColors.textContent =
    "Selection: white halo = you · blue = friend · amber = enemy. “Freeze topology” locks the graph, not motion.";

  const hDisturb = document.createElement("h3");
  hDisturb.textContent = "Disturb";
  const pDisturb = document.createElement("p");
  pDisturb.textContent = DISTURB_HELP.detail;

  const hScript = document.createElement("h3");
  hScript.textContent = "Script";
  const pScript = document.createElement("p");
  pScript.textContent =
    "Pick a recipe — it runs immediately and stays selected. Replay runs it again. Script… clears.";

  const ul = document.createElement("ul");
  for (const s of BUILTIN_SCRIPTS) {
    const li = document.createElement("li");
    const name = document.createElement("strong");
    name.textContent = s.label;
    li.append(name, document.createTextNode(` — ${s.summary}`));
    if (s.durationHint) {
      const dur = document.createElement("span");
      dur.className = "tip-muted";
      dur.textContent = ` (${s.durationHint})`;
      li.append(dur);
    }
    li.title = s.detail;
    ul.append(li);
  }

  const foot = document.createElement("p");
  foot.className = "tip-muted";
  foot.textContent = "Hover a list item for full detail.";

  host.append(
    hTool,
    pTool,
    toolUl,
    hMode,
    modeUl,
    pColors,
    hDisturb,
    pDisturb,
    hScript,
    pScript,
    ul,
    foot,
  );
}

export function bindTransportTip(
  tipBtn: HTMLButtonElement,
  tipPop: HTMLElement,
): void {
  fillTransportTip(tipPop);
  const placeTip = (): void => {
    const br = tipBtn.getBoundingClientRect();
    const margin = 8;
    const maxW = Math.min(22 * 16, window.innerWidth - margin * 2);
    tipPop.style.position = "fixed";
    tipPop.style.left = "auto";
    tipPop.style.right = "auto";
    tipPop.style.bottom = "auto";
    tipPop.style.transform = "none";
    tipPop.style.width = `${maxW}px`;
    const spaceAbove = br.top - margin;
    const spaceBelow = window.innerHeight - br.bottom - margin;
    const preferAbove = spaceAbove >= 160 || spaceAbove >= spaceBelow;
    tipPop.style.maxHeight = `${Math.min(22 * 16, preferAbove ? spaceAbove : spaceBelow)}px`;
    const pr = tipPop.getBoundingClientRect();
    let left = br.left + br.width / 2 - pr.width * 0.3;
    left = Math.max(
      margin,
      Math.min(left, window.innerWidth - pr.width - margin),
    );
    tipPop.style.left = `${left}px`;
    if (preferAbove) {
      tipPop.style.top = "auto";
      tipPop.style.bottom = `${window.innerHeight - br.top + 6}px`;
    } else {
      tipPop.style.bottom = "auto";
      tipPop.style.top = `${br.bottom + 6}px`;
    }
  };
  const setTipOpen = (open: boolean): void => {
    tipBtn.setAttribute("aria-expanded", open ? "true" : "false");
    tipPop.hidden = !open;
    if (open) {
      requestAnimationFrame(() => {
        placeTip();
        requestAnimationFrame(placeTip);
      });
    }
  };
  tipBtn.addEventListener("click", (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setTipOpen(tipPop.hidden);
  });
  tipBtn.addEventListener("pointerdown", (ev) => {
    ev.stopPropagation();
  });
  document.addEventListener("pointerdown", (ev) => {
    if (tipPop.hidden) return;
    const t = ev.target as Node | null;
    if (t && !tipPop.contains(t) && t !== tipBtn && !tipBtn.contains(t)) {
      setTipOpen(false);
    }
  });
  window.addEventListener(
    "resize",
    () => {
      if (!tipPop.hidden) placeTip();
    },
    { passive: true },
  );
  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && !tipPop.hidden) {
      ev.preventDefault();
      setTipOpen(false);
      tipBtn.focus();
      return;
    }
    if (ev.key === "?" || (ev.key === "/" && ev.shiftKey)) {
      const tag = (ev.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if ((ev.target as HTMLElement | null)?.isContentEditable) return;
      ev.preventDefault();
      setTipOpen(tipPop.hidden);
    }
  });
}
